import { formatCustomValue, type CustomFieldDto, type LeadDetail } from '@performance-crm/shared';
import { ListPlus, Pencil } from 'lucide-react';
import { Fragment, useState, type FormEvent } from 'react';
import { CustomFieldInputs } from '@/components/leads/CustomFieldInputs';
import { customFormErrors, customFormValues, customPayload } from '@/components/leads/customFields';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast-context';
import { ApiRequestError } from '@/lib/api';
import { useCatalogs, useUpdateLead } from '@/lib/queries';

/** Campos personalizados de la empresa con sus valores para este lead; se oculta si la empresa no definió ninguno. */
export function CustomFieldsCard({ lead, canEdit }: { lead: LeadDetail; canEdit: boolean }) {
  const { data: catalogs } = useCatalogs(lead.companyId);
  const fields = catalogs?.customFields ?? [];
  const [editing, setEditing] = useState(false);
  if (fields.length === 0) return null;
  return (
    <Card data-testid="custom-fields-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <ListPlus className="h-4 w-4" /> Datos adicionales
          </span>
          {canEdit && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditing(true)}
              data-testid="btn-edit-custom"
            >
              <Pencil className="h-4 w-4" /> Editar
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          {fields.map((f) => (
            <Fragment key={f.key}>
              <dt className="text-muted-foreground">{f.label}</dt>
              <dd className="font-medium" data-testid={`custom-value-${f.key}`}>
                {formatCustomValue(f, lead.custom[f.key])}
              </dd>
            </Fragment>
          ))}
        </dl>
      </CardContent>
      {editing && (
        <CustomFieldsDialog lead={lead} fields={fields} onClose={() => setEditing(false)} />
      )}
    </Card>
  );
}

function CustomFieldsDialog({
  lead,
  fields,
  onClose,
}: {
  lead: LeadDetail;
  fields: CustomFieldDto[];
  onClose: () => void;
}) {
  const mutation = useUpdateLead(lead.id);
  const toast = useToast();
  const [values, setValues] = useState(() => customFormValues(fields, lead.custom));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = customFormErrors(fields, values);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      await mutation.mutateAsync({ custom: customPayload(fields, values) });
      toast.success('Datos adicionales guardados');
      onClose();
    } catch (err) {
      const details =
        err instanceof ApiRequestError
          ? (err.body as { details?: { fields?: Record<string, string> } }).details
          : undefined;
      if (details?.fields) setErrors(details.fields);
      else toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form onSubmit={(e) => void submit(e)} className="space-y-3">
          <DialogHeader>
            <DialogTitle>Datos adicionales</DialogTitle>
            <DialogDescription>Campos que definió tu empresa para sus leads.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <CustomFieldInputs
              fields={fields}
              values={values}
              errors={errors}
              onChange={(key, value) => setValues((v) => ({ ...v, [key]: value }))}
              idPrefix="edit-cf"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending} data-testid="btn-save-custom">
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
